import { DroughtMetrics, HeatMetrics, RiskScenario, Site, PluvialFloodMetrics, YearData, SectorHazards } from 'src/api/Api';
import { AnalysisStatus } from 'src/enum';

/**
 * 
 * @returns {string | null} - The active parent path based on the current URL.
 * If no match is found, returns null.
 */
export const getActiveParent = (path: string): string | null => {
    if (path.includes('/profile/update-user') || path.includes('/profile/change-password')) {
        return null; // Exclude user account and password change routes
    }
    if (path.includes('/carbon')) return 'carbon';
    if (path.includes('/bio-diversity')) return 'bio-diversity';
    if (path.includes('/environment')) return 'environment';
    if (path.startsWith('/project')) return 'project';
    return null;
};

export const isSuccessCarbonAnalysis = (site: Site | null | undefined): boolean => {
    if (!site) return false;
    return !!(site.carbon_analysis_enabled && site.carbon_analysis_status === AnalysisStatus.COMPLETED);
};

export const isSuccessBioAnalysis = (site: Site | null | undefined): boolean => {
    if (!site) return false;
    return !!(site.bio_analysis_enabled && site.bio_analysis_status === AnalysisStatus.COMPLETED);
};

export const isSuccessEnvAnalysis = (site: Site | null | undefined): boolean => {
    if (!site) return false;
    return !!(site.env_analysis_enabled && site.env_analysis_status === AnalysisStatus.COMPLETED);
};

/**
 * Calculates the crediting period in years based on start and end dates.
 * @param {string | undefined} startDate - The start date string (e.g., "YYYY-MM-DD").
 * @param {string | undefined} endDate - The end date string (e.g., "YYYY-MM-DD").
 * @returns {string | null} - The formatted crediting period string or null if dates are invalid.
 */
export const calculateCreditingPeriod = (startDate: string | null | undefined, endDate: string | null | undefined): string | null => {
    if (!startDate || !endDate) {
        return null;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
        return null;
    }

    const diffInMilliseconds = end.getTime() - start.getTime();
    const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);

    let years = end.getFullYear() - start.getFullYear();
    const startMonthDay = start.getMonth() * 100 + start.getDate();
    const endMonthDay = end.getMonth() * 100 + end.getDate();

    if (endMonthDay < startMonthDay) {
        years--;
    }

    // Special handling for leap year anniversary if the end date is Feb 28 in a non-leap year
    // and the start date was Feb 29 in a leap year.
    if (years === 0 && diffInDays >= 365 && start.getMonth() === 1 && start.getDate() === 29 && end.getMonth() === 1 && end.getDate() === 28) {
        return '1 Year';
    }

    if (years < 1) return '0 Year';
    if (years === 1) return '1 Year';
    return `${years} Years`;
};

/**
 * Rounds a numeric value to two decimal places.
 * If the input is not a number or a string parsable to a number, it returns the original value.
 * Integers or numbers already at two or fewer decimal places are returned as is (e.g., 5 -> 5, 5.1 -> 5.1).
 * @param {unknown} value - The value to round.
 * @returns {number | unknown} - The rounded number or the original value.
 */
export const roundToTwoDecimalPlaces = (value: string | number | null | undefined): string | number => {
    let numToRound: number;

    if (typeof value === 'number') {
        numToRound = value;
    } else if (typeof value === 'string') {
        const parsedNum = parseFloat(value);
        if (!isNaN(parsedNum)) {
            numToRound = parsedNum;
        } else {
            return value; // Not a parsable string, return as is
        }
    } else {
        return value ? value : `${value}`;
    }
    // Round to two decimal places
    return Math.round((numToRound + Number.EPSILON) * 100) / 100;
};


type HazardType = "heat" | "drought" | "pluvial_flood_rain";

const hazards: HazardType[] = ["heat", "drought", "pluvial_flood_rain"];


  // --------------------------
  // Extract Heatmap Matrix
  // --------------------------
export function extractHeatmapData(
    scenario: RiskScenario,
    sector: string,
    metric:
      | keyof HeatMetrics
      | keyof DroughtMetrics
      | keyof PluvialFloodMetrics = "productivity_var_percent"
  ) {
    const years = Object.keys(scenario.years).sort();
    const matrix: number[][] = hazards.map(() => years.map(() => 0));

    years.forEach((year, yIdx) => {
      const yearData: YearData = scenario.years[year];
      const sectorHazards: SectorHazards | undefined = yearData?.sectors?.[sector];

      hazards.forEach((hazard, hIdx) => {
        const hazardData = sectorHazards?.[hazard];
        const value = (hazardData as any)?.[metric];
        matrix[hIdx][yIdx] = value ?? 0;
      });
    });

    return { years, hazards, matrix };
  }

  // --------------------------
  // Extract Sectors
  // --------------------------
export function extractSectorList(scenarios: RiskScenario[]): string[] {
    if (!scenarios.length) return [];

    const first = scenarios[0];
    const firstYear = Object.keys(first.years)[0];
    if (!firstYear) return [];

    return Object.keys(first.years[firstYear].sectors);
  }

  // --------------------------
  // Load Data from App State
  // --------------------------

export function extractRiskIncreaseLines(
    scenario: RiskScenario,
    sector: string
  ) {
    const years = Object.keys(scenario.years).sort();
    const hazards: HazardType[] = ["heat", "drought", "pluvial_flood_rain"];

    const series = hazards.map((hazard) => ({
      name: hazard,
      data: years.map((year) => {
        const hazardData = scenario.years[year]?.sectors?.[sector]?.[hazard];
        return hazardData?.risk_increase_percent ?? 0;
      }),
    }));

    return { years, series };
  }
